/**
 * Работа с `view.config.columns` таблицы.
 *
 * В массиве вперемешку лежат колонки двух родов:
 *  - field-колонки `{ fieldSlug, width?, pinned?, aggregate? }` — поля типа записи;
 *  - view-only колонки `{ kind, key, ... }` — из связей (`link_aggregate`),
 *    расчётные (`view_computed`) и атрибуты справочников (`reference_attribute`).
 *
 * Модалка «Настройки представления» редактирует только набор и порядок
 * field-колонок. Остальные надо переносить в новый конфиг как есть: если
 * собрать колонки как `{ fieldSlug }` по списку слагов, view-only колонки
 * превращаются в `{}` и пропадают вместе с сортировкой/группировкой по ним.
 */

export type SavedColumn = Record<string, unknown>;

export function isFieldColumn(c: SavedColumn): c is SavedColumn & { fieldSlug: string } {
  return typeof c.fieldSlug === 'string' && c.fieldSlug.length > 0;
}

export function isViewOnlyColumn(c: SavedColumn): c is SavedColumn & { kind: string; key: string } {
  return typeof c.kind === 'string' && c.kind !== 'field' && typeof c.key === 'string' && c.key.length > 0;
}

/** Нормализует сырой `config.columns`: только объекты. */
export function readSavedColumns(raw: unknown): SavedColumn[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((c): c is SavedColumn => !!c && typeof c === 'object');
}

/**
 * Собирает новый `columns` для сохранения: field-колонки в порядке `visibleSlugs`
 * (с сохранением width/pinned/aggregate из старого конфига), затем все view-only
 * колонки старого конфига без изменений. Пустые/битые записи отбрасываются.
 */
export function buildColumnsConfig(saved: SavedColumn[], visibleSlugs: string[]): SavedColumn[] {
  const fieldColumns = saved.filter(isFieldColumn);
  return [
    ...visibleSlugs.map((slug) => fieldColumns.find((c) => c.fieldSlug === slug) ?? { fieldSlug: slug }),
    ...saved.filter(isViewOnlyColumn),
  ];
}
